import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { faMusic } from '@fortawesome/pro-light-svg-icons/faMusic';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { addSongFlowMessages } from '../addSongFlowMessages';
import { PagesMeter } from './doneFreeLimits';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The first card of Done (design f1-f3): the action with the most value for this song. */
export type DoneLead = 'presave' | 'smartLink' | 'kit';

interface DoneNextCardsProps {
  title: string;
  lead: DoneLead;
  /**
   * Free promotes one release at a time (`activeReleasesLimit` 1): the line on the kit card (design
   * f3), with the name of the other active release when there is one. Undefined: no line.
   */
  kitNote: string | undefined;
  /** The published pages meter of the pre-save and SmartLink cards (design f3); undefined: none. */
  pagesMeter: PagesMeter | undefined;
  /**
   * "On Artist, $19 a month" on a plan with no pre-saves (design presave-subscriptions d5): the
   * pre-save card keeps its place with this chip, and its click opens the plan prompt. Null: no lock.
   */
  presaveLock: string | null;
  onPresave: () => void;
  onSmartLink: () => void;
  onKit: () => void;
  onSongPage: () => void;
}

interface Card {
  key: string;
  icon: IconDefinition;
  title: string;
  body: string;
  button: string;
  onClick: () => void;
  note?: string;
  /** True for a card that makes a published page: it shows the pages meter. */
  makesPage?: boolean;
  /** The plan chip of a locked card. */
  lock?: string;
}

/** The three next actions of Done (design catalog-add-song f1-f3). */
const DoneNextCardsComponent: React.FC<DoneNextCardsProps> = ({
  title,
  lead,
  kitNote,
  pagesMeter,
  presaveLock,
  onPresave,
  onSmartLink,
  onKit,
  onSongPage,
}) => {
  const intl = useIntl();
  const cards = useMemo<Card[]>(() => {
    const presave: Card = {
      key: 'presave',
      icon: faCheck,
      title: intl.formatMessage(addSongFlowMessages.donePresave),
      body: intl.formatMessage(addSongFlowMessages.donePresaveBody, { title }),
      button: intl.formatMessage(addSongFlowMessages.donePresaveButton),
      onClick: onPresave,
      makesPage: presaveLock === null,
      lock: presaveLock ?? undefined,
    };
    const smartLink: Card = {
      key: 'smartLink',
      icon: faLink,
      title: intl.formatMessage(addSongFlowMessages.doneSmartLink),
      body: intl.formatMessage(addSongFlowMessages.doneSmartLinkBody),
      button: intl.formatMessage(addSongFlowMessages.doneSmartLinkButton),
      onClick: onSmartLink,
      makesPage: true,
    };
    const kit: Card = {
      key: 'kit',
      icon: faSparkles,
      title: intl.formatMessage(addSongFlowMessages.doneKit),
      body: intl.formatMessage(addSongFlowMessages.doneKitBody),
      button: intl.formatMessage(addSongFlowMessages.doneKitButton),
      onClick: onKit,
      note: kitNote,
    };
    const songPage: Card = {
      key: 'songPage',
      icon: faMusic,
      title: intl.formatMessage(addSongFlowMessages.doneSongPage),
      body: intl.formatMessage(addSongFlowMessages.doneSongPageBody),
      button: intl.formatMessage(addSongFlowMessages.doneSongPageButton),
      onClick: onSongPage,
    };
    if (lead === 'presave') {
      return [presave, kit, songPage];
    }
    if (lead === 'smartLink') {
      return [smartLink, kit, songPage];
    }
    return [kit, smartLink, songPage];
  }, [intl, kitNote, lead, onKit, onPresave, onSmartLink, onSongPage, presaveLock, title]);

  let meterText: string | undefined;
  if (pagesMeter !== undefined) {
    meterText = intl.formatMessage(
      pagesMeter.isFull ? addSongFlowMessages.donePagesFull : addSongFlowMessages.donePagesMeter,
      { used: pagesMeter.used, limit: pagesMeter.limit }
    );
  }
  const meterPercent =
    pagesMeter === undefined || pagesMeter.limit <= 0
      ? 0
      : Math.min(100, Math.round((pagesMeter.used / pagesMeter.limit) * 100));

  return (
    <div className={styles.nextGrid}>
      {cards.map((card, index) => (
        <div
          key={card.key}
          className={clsx(styles.next, index === 0 && styles.nextPrimary)}
          data-testid={`add-song-done-${card.key}`}
        >
          <span className={clsx(styles.kbox, index === 0 && styles.kboxBrand)}>
            <FontAwesomeIcon icon={card.icon} />
          </span>
          <b>{card.title}</b>
          <small>{card.body}</small>
          {card.makesPage === true && meterText !== undefined && (
            <div data-testid={`add-song-done-${card.key}-meter`}>
              <div className={styles.meterLine}>
                <span>{intl.formatMessage(addSongFlowMessages.donePagesLabel)}</span>
                <span>{meterText}</span>
              </div>
              <div className={styles.meter}>
                {/* The fill width is the share of the pages in use: a value, not a layout size. */}
                <span className={styles.meterFill} style={{ width: `${meterPercent}%` }} />
              </div>
            </div>
          )}
          {card.note !== undefined && <div className={styles.lockLine}>{card.note}</div>}
          {card.lock !== undefined && (
            <div>
              <span className={styles.lockChip} data-testid={`add-song-done-${card.key}-lock`}>
                {card.lock}
              </span>
            </div>
          )}
          <SMLButton
            size="sm"
            color={index === 0 ? 'primary' : undefined}
            outline={index !== 0}
            omitCap
            onClick={card.onClick}
          >
            {card.button}
          </SMLButton>
        </div>
      ))}
    </div>
  );
};

export const DoneNextCards = TypedMemo(DoneNextCardsComponent);
